import { useState } from 'react';
import { Plus, Trash2, Pencil, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { Card } from '../ui/Card';
import { Avatar } from '../ui/Avatar';
import { FaClock, FaFileInvoice, FaPen, FaPlus, FaTrashCan } from '../ui/FaIcons';
import { TimeEntryModal } from './TimeEntryModal';
import { GenerateInvoiceModal } from '../billing/GenerateInvoiceModal';
import {
  useTimeEntries,
  useDeleteTimeEntry,
  type TimeEntry,
} from '../../hooks/queries/useTimeEntries';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import {
  formatDuration,
  computeAmount,
} from '../../services/timeTracking/timeTrackingService';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';

import { formatAppDate } from '../../lib/dateFormat';
import { ResizableColumns } from '../ui/ResizableColumns';
interface Props {
  dossierId: string;
  /** Lecture seule si dossier archivé/clos */
  readOnly?: boolean;
  /** Intégré à l'onglet Facturation : sans grand titre ni cartes de totaux. */
  embedded?: boolean;
}

export function DossierTimeEntriesTab({ dossierId, readOnly = false, embedded = false }: Props) {
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const canCreate =
    !readOnly && canAccessFeature(role, 'time_entries_create');

  const { data: entries, isLoading } = useTimeEntries({ dossier_id: dossierId });
  const { data: collaborators } = useCollaborators();
  const deleteMut = useDeleteTimeEntry();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const canGenerateInvoice =
    !readOnly && canAccessFeature(role, 'billing_create');
  const hasUninvoicedBillable = (entries ?? []).some(
    (e) => e.is_billable && !e.invoiced_in_id,
  );

  const totals = (entries ?? []).reduce(
    (acc, e) => {
      acc.minutes += e.duration_minutes;
      const amount = computeAmount(e);
      if (amount !== null) acc.amount += amount;
      if (e.is_billable) acc.billableMinutes += e.duration_minutes;
      return acc;
    },
    { minutes: 0, billableMinutes: 0, amount: 0 },
  );

  function getCollabName(id: string): string {
    return collaborators?.find((c) => c.id === id)?.full_name ?? 'Inconnu';
  }

  async function handleDelete(entry: TimeEntry) {
    if (!confirm('Supprimer cette prestation ?')) return;
    try {
      await deleteMut.mutateAsync(entry.id);
      toast.success('Prestation supprimée');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${message}`);
    }
  }

  const modals = (
    <>
      {/* Modale */}
      {modalOpen && (
        <TimeEntryModal
          open={modalOpen}
          onOpenChange={(o) => {
            setModalOpen(o);
            if (!o) setEditing(null);
          }}
          dossierId={dossierId}
          entry={editing}
        />
      )}

      {/* Wizard génération facture */}
      {invoiceModalOpen && (
        <GenerateInvoiceModal
          open={invoiceModalOpen}
          onOpenChange={setInvoiceModalOpen}
          dossierId={dossierId}
        />
      )}
    </>
  );

  if (embedded) {
    const toBill = (entries ?? []).filter((e) => e.is_billable && !e.invoiced_in_id);
    const toBillMinutes = toBill.reduce((sum, e) => sum + e.duration_minutes, 0);
    const toBillAmount = toBill.reduce((sum, e) => sum + (computeAmount(e) ?? 0), 0);
    const euros = (value: number) =>
      new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
    const clock = (minutes: number) => `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;

    return (
      <div className="w-full">
        <header className="flex min-h-[52px] items-center justify-between gap-2 border-b border-[#e6e2d8] px-4 py-2.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
            Prestations
            <span className="ml-2 font-normal tabular-nums text-neutral-400">{entries?.length ?? 0}</span>
          </h3>
          {canCreate ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              <FaPlus className="h-3 w-3" aria-hidden /> Prestation
            </Button>
          ) : null}
        </header>

        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !entries || entries.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-neutral-400">
            Aucune prestation enregistrée pour ce dossier.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="cime-table text-[13px]">
              <thead>
                <tr className="text-left">
                  <th className="py-2 pl-4 pr-2.5">Prestation</th>
                  <th className="px-2.5 py-2">Intervenant</th>
                  <th className="px-2.5 py-2 text-right">Durée</th>
                  <th className="px-2.5 py-2 text-right">Montant HT</th>
                  <th className="py-2 pl-2.5 pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isMine = e.collaborator_id === collaborator?.id;
                  const canEdit = canCreate && isMine && !e.invoiced_in_id;
                  const amount = computeAmount(e);
                  const person = collaborators?.find((c) => c.id === e.collaborator_id);
                  const done = !!e.invoiced_in_id;
                  return (
                    <tr key={e.id} className="group">
                      <td className="max-w-[340px] py-2.5 pl-4 pr-2.5 leading-tight">
                        <span className={`block truncate text-sm ${done ? 'text-cime-text-muted' : 'text-cime-text-primary'}`}>
                          {e.description || '—'}
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-400">
                          <FaClock className="h-2.5 w-2.5 flex-shrink-0" aria-hidden />
                          {e.billing_code || 'Temps passé'}
                          {done ? (
                            <span className="font-medium text-[#2f7a55]">· Facturée</span>
                          ) : !e.is_billable ? (
                            <span>· Non facturable</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-2.5 py-2.5">
                        <CollaboratorLink collaboratorId={e.collaborator_id} title={`Voir le profil de ${getCollabName(e.collaborator_id)}`}>
                          <Avatar
                            name={getCollabName(e.collaborator_id)}
                            src={person?.avatar_url ?? undefined}
                            size="xs"
                            className="h-[26px] w-[26px] text-[10px]"
                          />
                        </CollaboratorLink>
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums text-cime-text-primary">
                        {clock(e.duration_minutes)}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-2.5 text-right tabular-nums text-cime-text-primary">
                        {amount !== null ? euros(amount) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="whitespace-nowrap py-2.5 pl-2.5 pr-4 text-cime-text-primary">
                        <span className="flex items-center justify-between gap-2">
                          {formatAppDate(e.started_at, { year: false })}
                          {canEdit ? (
                            <span className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                onClick={() => { setEditing(e); setModalOpen(true); }}
                                className="rounded p-1 text-neutral-400 hover:text-cime-primary"
                                aria-label="Modifier"
                                title="Modifier"
                              >
                                <FaPen className="h-3 w-3" aria-hidden />
                              </button>
                              <button
                                onClick={() => handleDelete(e)}
                                className="rounded p-1 text-neutral-400 hover:text-[#c05b33]"
                                aria-label="Supprimer"
                                title="Supprimer"
                              >
                                <FaTrashCan className="h-3 w-3" aria-hidden />
                              </button>
                            </span>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {entries && entries.length > 0 ? (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e6e2d8] px-4 py-3 text-sm text-cime-text-primary">
            <span>
              {toBill.length} ligne{toBill.length > 1 ? 's' : ''} à facturer
              {toBill.length > 0 ? (
                <>
                  {' · '}<b className="font-semibold">{clock(toBillMinutes)}</b>
                  {' · '}<b className="font-semibold">{euros(toBillAmount)} HT</b>
                </>
              ) : null}
            </span>
            {canGenerateInvoice && hasUninvoicedBillable ? (
              <Button variant="primary" onClick={() => setInvoiceModalOpen(true)}>
                <FaFileInvoice className="h-3.5 w-3.5" aria-hidden /> Générer une facture
              </Button>
            ) : null}
          </footer>
        ) : null}

        {modals}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        {embedded ? (
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
            Prestations
            <span className="ml-2 font-normal normal-case tracking-normal text-neutral-400">
              {totals.billableMinutes > 0 ? `${formatDuration(totals.billableMinutes)} facturables` : ''}
            </span>
          </h3>
        ) : (
          <div>
            <h2 className="text-xl font-bold text-cime-primary">
              Prestations
            </h2>
            <p className="text-sm text-neutral-500">
              Temps passé sur ce dossier — saisie manuelle (D-04). Le chronomètre arrive en PR2.
            </p>
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {canGenerateInvoice && hasUninvoicedBillable && (
            <Button variant="secondary" onClick={() => setInvoiceModalOpen(true)}>
              <ReceiptText className="w-4 h-4 mr-1.5" /> Générer une facture
            </Button>
          )}
          {canCreate && (
            <Button
              variant="primary"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nouvelle prestation
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className={embedded ? 'hidden' : 'grid grid-cols-3 gap-3'}>
        <Card>
          <p className="text-xs text-neutral-500 uppercase tracking-wide">Temps total</p>
          <p className="text-2xl font-bold text-neutral-900">
            {totals.minutes > 0 ? formatDuration(totals.minutes) : '—'}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-neutral-500 uppercase tracking-wide">Dont facturable</p>
          <p className="text-2xl font-bold text-neutral-900">
            {totals.billableMinutes > 0 ? formatDuration(totals.billableMinutes) : '—'}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-neutral-500 uppercase tracking-wide">Montant HT</p>
          <p className="text-2xl font-bold text-neutral-900">
            {totals.amount > 0
              ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totals.amount)
              : '—'}
          </p>
        </Card>
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !entries || entries.length === 0 ? (
        <Card>
          <p className="text-sm text-neutral-500 text-center py-6">
            Aucune prestation enregistrée pour ce dossier.
          </p>
        </Card>
      ) : (
        <Card>
          <table className="cime-table text-sm">
            <ResizableColumns id="dossier-time-entries" />
            <thead>
              <tr className="text-left">
                <th className="py-2 px-2">Date</th>
                <th className="py-2 px-2">Avocat</th>
                <th className="py-2 px-2">Durée</th>
                <th className="py-2 px-2">Code</th>
                <th className="py-2 px-2">Description</th>
                <th className="py-2 px-2 text-right">Montant</th>
                <th className="py-2 px-2 text-right w-20">Statut</th>
                {canCreate && <th className="py-2 px-2 w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const isMine = e.collaborator_id === collaborator?.id;
                const canEdit = canCreate && isMine && !e.invoiced_in_id;
                const amount = computeAmount(e);
                return (
                  <tr key={e.id}>
                    <td className="py-2 px-2 text-neutral-700">
                      {formatAppDate(e.started_at)}
                    </td>
                    <td className="py-2 px-2 text-neutral-700 truncate max-w-[140px]">
                      <CollaboratorLink collaboratorId={e.collaborator_id} title={`Voir le profil de ${getCollabName(e.collaborator_id)}`}>
                        {getCollabName(e.collaborator_id)}
                      </CollaboratorLink>
                    </td>
                    <td className="py-2 px-2 tabular-nums text-neutral-900">
                      {formatDuration(e.duration_minutes)}
                    </td>
                    <td className="py-2 px-2">
                      {e.billing_code ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">
                          {e.billing_code}
                        </span>
                      ) : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="py-2 px-2 text-neutral-600 truncate max-w-[260px]">
                      {e.description ?? '—'}
                    </td>
                    <td className="py-2 px-2 text-right cime-amount">
                      {amount !== null
                        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount)
                        : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="py-2 px-2 text-right">
                      {e.invoiced_in_id ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                          Facturée
                        </span>
                      ) : e.is_billable ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                          À facturer
                        </span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                          Non factu.
                        </span>
                      )}
                    </td>
                    {canCreate && (
                      <td className="py-2 px-2">
                        {canEdit && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => { setEditing(e); setModalOpen(true); }}
                              className="p-1 text-neutral-400 hover:text-cime-primary rounded"
                              aria-label="Modifier"
                              title="Modifier"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(e)}
                              className="p-1 text-neutral-400 hover:text-red-600 rounded"
                              aria-label="Supprimer"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {modals}
    </div>
  );
}
